import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AdminUser {
  id: string;
  email: string;
  prenom?: string;
  nom?: string;
  role: string;
  created_at: string;
  last_sign_in_at?: string;
  abonnements?: {
    plan: string;
    statut: string;
    date_debut: string;
    date_fin?: string;
  }[];
  bateaux?: {
    id: string;
    nom: string;
  }[];
}

export interface CreateUserData {
  email: string;
  password: string;
  prenom?: string;
  nom?: string;
  plan?: string;
  formule?: 'gratuit' | 'mensuel' | 'annuel' | 'partenaire';
  date_debut?: string | null;
  date_fin?: string | null;
}
export interface UpdateUserData {
  email: string;
  prenom?: string;
  nom?: string;
  role?: string;
  plan?: string;
  formule?: 'gratuit' | 'mensuel' | 'annuel' | 'partenaire';
  date_debut?: string | null;
  date_fin?: string | null;
}
export const useAdminUsers = () => {
  const { session } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Récupère un token frais (rafraîchi si nécessaire) pour éviter les 401 "session not found"
  const getFreshToken = async (): Promise<string | null> => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  };


  const checkAdminAccess = async () => {
    if (!session?.user) return false;
    
    try {
      // Utiliser la nouvelle table user_roles
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .maybeSingle();

      if (error) {
        console.error('Erreur vérification admin:', error);
        return false;
      }

      const adminAccess = !!data;
      setIsAdmin(adminAccess);
      return adminAccess;
    } catch (error) {
      console.error('Erreur vérification admin:', error);
      return false;
    }
  };

  const fetchUsers = async () => {
    const token = await getFreshToken();
    if (!token) return;

    const hasAccess = await checkAdminAccess();
    if (!hasAccess) {
      setLoading(false);
      return;
    }

    const fetchDirect = async () => {
      try {
        // Profils
        const { data: profiles, error: profilesErr } = await supabase
          .from('profils_utilisateur')
          .select('*')
          .order('created_at', { ascending: false });
        if (profilesErr || !profiles) throw profilesErr;

        const ids = profiles.map((p) => p.id);

        // Récupérer les rôles depuis user_roles
        let rolesByUser: Record<string, string> = {};
        if (ids.length > 0) {
          const { data: roles, error: rolesErr } = await supabase
            .from('user_roles')
            .select('user_id, role')
            .in('user_id', ids);
          if (!rolesErr && roles) {
            roles.forEach((r) => {
              rolesByUser[r.user_id] = r.role;
            });
          }
        }

        // Abonnements (dernier par user)
        let subsByUser: Record<string, any[]> = {};
        if (ids.length > 0) {
          const { data: subs, error: subsErr } = await supabase
            .from('abonnements')
            .select('user_id, plan, statut, date_debut, date_fin, created_at')
            .in('user_id', ids)
            .order('created_at', { ascending: false });
          if (subsErr) throw subsErr;
          subs?.forEach((s) => {
            if (!subsByUser[s.user_id]) subsByUser[s.user_id] = [{
              plan: s.plan, statut: s.statut, date_debut: s.date_debut, date_fin: s.date_fin ?? null,
            }];
          });
        }

        // Bateaux
        let boatsByUser: Record<string, any[]> = {};
        if (ids.length > 0) {
          const { data: boats, error: boatsErr } = await supabase
            .from('bateaux')
            .select('id, nom, user_id')
            .in('user_id', ids);
          if (boatsErr) throw boatsErr;
          boats?.forEach((b) => {
            if (!boatsByUser[b.user_id]) boatsByUser[b.user_id] = [];
            boatsByUser[b.user_id].push({ id: b.id, nom: b.nom });
          });
        }

        const result = profiles.map((p) => ({
          ...p,
          role: rolesByUser[p.id] || 'user',
          last_sign_in_at: null, // Le fallback direct ne peut pas accéder aux données auth
          abonnements: subsByUser[p.id] || [],
          bateaux: boatsByUser[p.id] || [],
        })) as AdminUser[];
        setUsers(result);
      } catch (e) {
        console.error('Erreur récupération directe utilisateurs:', e);
        toast.error('Erreur lors de la récupération des utilisateurs');
      } finally {
        setLoading(false);
      }
    };

    try {
      const { data, error } = await supabase.functions.invoke('admin-users', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (error) {
        console.warn('Invoke admin-users a échoué, tentative avec session rafraîchie:', error);
        // Retenter une fois avec une session explicitement rafraîchie
        const { data: refreshed } = await supabase.auth.refreshSession();
        const newToken = refreshed.session?.access_token;
        if (newToken) {
          const retry = await supabase.functions.invoke('admin-users', {
            headers: {
              Authorization: `Bearer ${newToken}`,
              'Content-Type': 'application/json',
            },
          });
          if (!retry.error) {
            setUsers(retry.data || []);
            setLoading(false);
            return;
          }
          console.warn('Retry admin-users a échoué, fallback direct:', retry.error);
        }
        toast.warning("Données auth indisponibles : dernières connexions non affichées");
        return await fetchDirect();
      }


      setUsers(data || []);
      setLoading(false);
    } catch (error) {
      console.warn('Invoke admin-users exception, fallback direct:', error);
      await fetchDirect();
    }
  };

  const createUser = async (userData: CreateUserData) => {
    const token = await getFreshToken();
    if (!token) {
      toast.error('Non authentifié');
      return false;
    }

    try {
      const { data, error } = await supabase.functions.invoke('admin-users', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: userData,
      });

      if (error) {
        console.error('Erreur création utilisateur:', error);
        toast.error('Erreur lors de la création de l\'utilisateur');
        return false;
      }

      if (data.success) {
        toast.success('Utilisateur créé avec succès');
        await fetchUsers(); // Recharger la liste
        return true;
      }

      return false;
    } catch (error) {
      console.error('Erreur création utilisateur:', error);
      toast.error('Erreur lors de la création de l\'utilisateur');
      return false;
    }
  };

  const updateUser = async (userId: string, userData: UpdateUserData) => {
    const token = await getFreshToken();
    if (!token) {
      toast.error('Non authentifié');
      return false;
    }

    console.log('updateUser called with:', { userId, userData });

    try {
      const { data, error } = await supabase.functions.invoke('admin-users', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-method-override': 'PUT',
        },
        body: {
          method: 'PUT',
          userId,
          userData,
        },
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.warn('Invoke admin-users (PUT) a échoué, fallback direct:', error);
        // Fallback direct DB
        const { error: profileErr } = await supabase
          .from('profils_utilisateur')
          .update({
            email: userData.email,
            prenom: userData.prenom,
            nom: userData.nom,
          })
          .eq('id', userId);
        if (profileErr) throw profileErr;

        // Mettre à jour le rôle dans user_roles si spécifié
        if (userData.role) {
          // D'abord supprimer l'ancien rôle
          await supabase
            .from('user_roles')
            .delete()
            .eq('user_id', userId);
          
          // Puis insérer le nouveau rôle
          const { error: roleErr } = await supabase
            .from('user_roles')
            .insert({
              user_id: userId,
              role: userData.role as any,
            });
          if (roleErr) console.warn('Erreur mise à jour rôle:', roleErr);
        }

        // Préparer la mise à jour de l'abonnement
        const updateFields: Record<string, any> = {};
        if (userData.plan) updateFields.plan = userData.plan;

        const normalizeStart = (start: string) => {
          if (!start) return undefined as Date | undefined;
          const s = start.includes('T') ? start : `${start}T12:00:00Z`;
          const d = new Date(s);
          return isNaN(d.getTime()) ? undefined : d;
        };

        const computeDateFin = (startISO: string, formule?: 'gratuit' | 'mensuel' | 'annuel' | 'partenaire') => {
          if (!startISO || !formule || formule === 'gratuit') return undefined as string | undefined;
          const base = normalizeStart(startISO);
          if (!base) return undefined;
          if (formule === 'mensuel') base.setUTCMonth(base.getUTCMonth() + 1);
          if (formule === 'annuel') base.setUTCFullYear(base.getUTCFullYear() + 1);
          if (formule === 'partenaire') base.setUTCMonth(base.getUTCMonth() + 3);
          return base.toISOString();
        };

        const hasExplicitStart =
          userData.date_debut !== undefined && userData.date_debut !== null && userData.date_debut !== '';

        if (hasExplicitStart) {
          updateFields.date_debut = userData.date_debut;
        } else if (userData.formule) {
          const now = new Date();
          const todayNoonUTC = new Date(
            Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12, 0, 0)
          );
          updateFields.date_debut = todayNoonUTC.toISOString();
        }

        if (userData.date_fin !== undefined) {
          updateFields.date_fin = userData.date_fin;
        } else if (updateFields.date_debut) {
          const autoFin = computeDateFin(updateFields.date_debut, userData.formule);
          if (autoFin) updateFields.date_fin = autoFin;
        }

        console.log('Fallback: updating subscription with fields:', updateFields);

        if (Object.keys(updateFields).length > 0) {
          const { data: subs, error: subsErr } = await supabase
            .from('abonnements')
            .select('id')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(1);
          if (subsErr) throw subsErr;
          if (subs && subs.length > 0) {
            console.log('Updating existing subscription:', subs[0].id);
            const { error: updateErr } = await supabase.from('abonnements').update(updateFields).eq('id', subs[0].id);
            if (updateErr) throw updateErr;
          } else {
            console.log('Creating new subscription');
            const { error: insertErr } = await supabase.from('abonnements').insert({
              user_id: userId,
              plan: userData.plan || 'premium',
              statut: 'actif',
              ...(updateFields.date_debut ? { date_debut: updateFields.date_debut } : {}),
              ...(updateFields.date_fin ? { date_fin: updateFields.date_fin } : {}),
            });
            if (insertErr) throw insertErr;
          }
        }

        toast.success('Utilisateur mis à jour avec succès');
        await fetchUsers();
        return true;
      }

      if (data?.success) {
        toast.success('Utilisateur mis à jour avec succès');
        await fetchUsers(); // Recharger la liste
        return true;
      } else if (data?.error) {
        console.error('Erreur mise à jour utilisateur (serveur):', data.error);
        toast.error(data.error);
        return false;
      }

      return false;
    } catch (error) {
      console.error('Erreur mise à jour utilisateur:', error);
      toast.error("Erreur lors de la mise à jour de l'utilisateur");
      return false;
    }
  };

  const deleteUser = async (userId: string) => {
    const token = await getFreshToken();
    if (!token) {
      toast.error('Non authentifié');
      return false;
    }

    try {
      const { data, error } = await supabase.functions.invoke('admin-users', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-method-override': 'DELETE',
        },
        body: {
          method: 'DELETE',
          userId,
        },
      });

      if (error) {
        console.warn('Invoke admin-users (DELETE) a échoué, fallback direct:', error);
        // Fallback: supprimer seulement le profil (l'utilisateur auth restera)
        const { error: profErr } = await supabase
          .from('profils_utilisateur')
          .delete()
          .eq('id', userId);
        if (profErr) {
          console.error('Erreur suppression profil (fallback):', profErr);
          toast.error("Suppression impossible");
          return false;
        }
        toast.success('Utilisateur supprimé (profil)');
        await fetchUsers();
        return true;
      }

      if (data?.success) {
        toast.success('Utilisateur supprimé avec succès');
        await fetchUsers(); // Recharger la liste
        return true;
      } else if (data?.error) {
        console.error('Erreur suppression utilisateur (serveur):', data.error);
        toast.error(data.error);
        return false;
      }

      return false;
    } catch (error) {
      console.error('Erreur suppression utilisateur:', error);
      toast.error("Erreur lors de la suppression de l'utilisateur");
      return false;
    }
  };

  useEffect(() => {
    if (session) {
      fetchUsers();
    }
  }, [session]);

  return {
    users,
    loading,
    isAdmin,
    createUser,
    updateUser,
    deleteUser,
    refetch: fetchUsers,
  };
};